import { useTranslation } from "react-i18next";
import Table from "../../../components/Common/dataTable/index";

import type { ColumnsType } from "antd/es/table";
import { formatToFrenchDate } from "../../../utils/dateUtils";
// import { useUser } from "context/UserContext";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import Badge from "components/Common/ui/Badge";
import UserStatusBadge from "components/Common/Badge/UserStatusBadge";
import UserRoleBadge from "components/Common/Badge/UserRoleBadge";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { getLabelRole } from "utils/stringUtils";
import { useAbility } from "config/abilityContext";
import { UserDetails } from "types/userManagementTypes";
import { all_routes } from "router/all_routes";



interface UserManagementTableProps {
  dataSource: UserDetails[];
  onEdit?: (record: UserDetails) => void;
  onDelete?: (id: string) => void;
  loading?: boolean;
}

const UserManagementTable = ({
  dataSource,
  onEdit,
  onDelete,
  loading,
}: UserManagementTableProps) => {
  const { confirm } = Modal;

  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.authSlice.user);
  const ability = useAbility();

  const columns: ColumnsType<UserDetails> = [
    {
      title: t("name"),
      dataIndex: "name",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: t("email"),
      dataIndex: "email",
    },
    {
      title: t("role"),
      dataIndex: "role",
      render: ( _: any, record: any) => {
        return (
          <UserRoleBadge
            role={getLabelRole(record.roles)}
            size="sm"
          />
        );
      },
    },
    {
      title: t("status"),
      dataIndex: "status",
      render: (status: string) => {
        return (
          <UserStatusBadge
            status={status as "active" | "inactive" | "deleted"}
          />
        );
      },
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      render: (date) => formatToFrenchDate(date),
    },
    {
      title: t("actions"),
      dataIndex: "actions",
      render: (_: any, record: UserDetails) => {
        if (user?.id?.toString() === record.id) {
          return <Badge variant="success" text={t("you")} />;
        }

        const actions: Action[] = [
          {
            label: t("edit"),
            icon: "ti ti-edit",
            hidden: !ability.can("update", "UserManagement"),
            actionType: "link",
            to: all_routes.UserManagementEdit.replace(":id", record.id),
          },
          {
            label: t("delete"),
            icon: "ti ti-trash",
            hidden: !ability.can("delete", "UserManagement"),
            actionType: "link",
            onClick: () => showDeleteConfirm(record.id),
          },
        ];
        return <ActionsDropdown actions={actions} />;
      },
    },
  ];

  const showDeleteConfirm = (id: string) => {
    confirm({
      title: t("are_you_sure_you_want_to_delete_this_user?"),
      icon: <ExclamationCircleOutlined />,
      content: t("this_action_cannot_be_undone"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      okButtonProps: { disabled: false },
      onOk: () => onDelete && onDelete(id),
    });
  };

  return <Table columns={columns} dataSource={dataSource} loading={loading} />;
};

export default UserManagementTable;
